import { formatDistance } from 'date-fns';
import { isNumber } from 'lodash';
import pluralize from 'pluralize';
import { memo } from 'react';

import { FileStatusIconProgressing, FileStatusSubtitle, FileStatusTitle } from '@air/feature-file-status/ui';
import { ONE_SECOND } from '@air/utilities';

import { getTaskPaneFileStatusIconSize } from '~/components/FileStatusTrackingPane/getTaskPaneFileStatusIconSize';
import { PaneItemTW } from '~/components/FileStatusTrackingPane/PaneItemTW';
import { TaskPaneDismissButton } from '~/components/FileStatusTrackingPane/TaskPaneDismissButton';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { type GetInProgressMetadata, type ZippingTask } from '~/store/tasks/types';

export type ZippingTaskInProgressPanelItemProps = {
  metadata: GetInProgressMetadata<ZippingTask>;
  onCancel: () => void;
};

type GetZippingInProgressSubtitleParams = {
  isUi2026: boolean;
  secondsRemaining: number | undefined;
};

const getZippingInProgressSubtitle = ({ isUi2026, secondsRemaining }: GetZippingInProgressSubtitleParams) => {
  if (!isNumber(secondsRemaining)) {
    return isUi2026 ? 'Starts when the archive is ready' : 'Your download will begin shortly.';
  }

  if (isUi2026) {
    return `About ${secondsRemaining} ${pluralize('second', secondsRemaining)} left`;
  }

  return `${formatDistance(new Date(Date.now() + secondsRemaining * ONE_SECOND), new Date(), {
    includeSeconds: true,
  })} left`;
};

export const ZippingTaskInProgressPanelItem = memo(
  ({
    onCancel,
    metadata: { clipIds = [], boardIds = [], collectionIds = [], secondsRemaining },
  }: ZippingTaskInProgressPanelItemProps) => {
    const { ui2026 } = useTemporaryFeatureFlags();
    const itemCount = clipIds.length + boardIds.length + collectionIds.length;
    const zippingTitle = `Zipping ${pluralize('item', itemCount, true)}`;
    const subtitle = getZippingInProgressSubtitle({ isUi2026: ui2026, secondsRemaining });

    return (
      <PaneItemTW
        avatar={<FileStatusIconProgressing size={getTaskPaneFileStatusIconSize(ui2026)} />}
        title={
          <FileStatusTitle data-testid="ZIPPING_PANEL_IN_PROGRESS_TITLE">
            {ui2026 ? `${zippingTitle}…` : zippingTitle}
          </FileStatusTitle>
        }
        subtitle={
          <FileStatusSubtitle className="truncate" data-testid="ZIPPING_PANEL_IN_PROGRESS_SUBTITLE">
            {subtitle}
          </FileStatusSubtitle>
        }
        buttons={<TaskPaneDismissButton label="Cancel" onClick={onCancel} />}
      />
    );
  },
);

ZippingTaskInProgressPanelItem.displayName = 'ZippingInProgressPanelItem';
